import { useState } from 'react';
import { Grid, Box, Typography } from '@mui/material';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../../redux/store';

import FormView from '../../components/views/formView';
import { Checkbox } from '../../components/general/fields/checkbox';
import FieldText from '../../components/general/fields/fieldText';
import { Dialog } from '../../components/general/dialog/dialog';
import FieldSelect from '../../components/general/fields/fieldSelect';
import { SwalCustomClass } from '../../components/general/toastSwals/swalCustomClass';
import { USEREDIT, USERLIST } from '../../../routes/paths';
import { UsersMessages } from '../../../shared/messages/usersMessages';
import { profiles } from '../../../shared/types/profiles';
import {
    asyncPatchtUser,
    asyncPostUser,
} from '../../../services/users.service';

type Props = {
    idUser?: any;
    user?: any;
    onSave?: any;
};

const UserForm = ({ user, idUser, onSave }: Props) => {
    const navigate = useNavigate();
    const { logUser } = useSelector((state: RootState) => state.general);
    const { id: idUserLog } = logUser || {};
    const { usersMessages }: any = UsersMessages;
    const [showDialog, setShowDialog] = useState(false); // Estado para controlar el diálogo

    const formik = useFormik({
        initialValues: {
            document: user !== undefined ? user.document : '',
            name: user !== undefined ? user.name : '',
            surname: user !== undefined ? user.surname : '',
            username: user !== undefined ? user.username : '',
            password: '',
            password_retry: '',
            password_change: user !== undefined ? user.password_change : false,
            type: user !== undefined ? user.type : '',
            status: user !== undefined ? user.status : '',
        },
        validationSchema: Yup.object({
            document: Yup.number()
                .typeError('El documento debe ser un número')
                .required('El documento es obligatorio'),
            name: Yup.string().required('El nombre es obligatorio'),
            surname: Yup.string().required('El apellido es obligatorio'),
            username: Yup.string()
                .email('El correo es invalido')
                .required('El correo de usuario es obligatorio')
                .test('Validate Email', 'El correo es inválido', (value) => {
                    const re =
                        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
                    return re.test(String(value).toLowerCase());
                }),
            password: Yup.string().when({
                is: () => Boolean(idUser === undefined),
                then: (schema) =>
                    schema
                        .required('La contraseña es obligatoria')
                        .min(
                            8,
                            'La contraseña debe tener al menos 8 caracteres'
                        )
                        .matches(
                            /^[a-zA-Z0-9!@#$%^&*()_+\-]+$/,
                            'La contraseña puede incluir caracteres alfanuméricos y los siguientes caracteres especiales: ! @ # $ % ^ & * ( ) _ + -'
                        )
                        .matches(
                            /[!@#$%^&*()_+\-]/,
                            'La contraseña debe contener al menos un carácter especial: ! @ # $ % ^ & * ( ) _ + -'
                        ),
                otherwise: (schema) =>
                    schema
                        .notRequired()
                        .min(
                            8,
                            'La contraseña debe tener al menos 8 caracteres'
                        )
                        .matches(
                            /^[a-zA-Z0-9!@#$%^&*()_+\-]+$/,
                            'La contraseña puede incluir caracteres alfanuméricos y los siguientes caracteres especiales: ! @ # $ % ^ & * ( ) _ + -'
                        )
                        .matches(
                            /[!@#$%^&*()_+\-]/,
                            'La contraseña debe contener al menos un carácter especial: ! @ # $ % ^ & * ( ) _ + -'
                        ),
            }),
            password_retry: Yup.string().when(['password'], {
                is: (password: any) =>
                    Boolean(idUser === undefined) || password !== undefined,
                then: (schema) =>
                    schema
                        .required(
                            'La confirmación de la contraseña es obligatoria'
                        )
                        .oneOf(
                            [Yup.ref('password')],
                            'Las contraseñas no coinciden'
                        ),
            }),
            type: Yup.string().required('El perfil es obligatorio'),
        }),
        onSubmit: async (formUser) => {
            if (idUser !== undefined) {
                asyncPatchtUser(idUser, formUser).then((body: any) => {
                    if (body?.items !== undefined) {
                        onSave(true);
                        navigate(`${USEREDIT}/${body.items.id}`);
                        SwalCustomClass.fire({
                            text: usersMessages.MESSAGE_EDIT,
                            icon: 'success',
                        });
                    } else if (body?.errors !== undefined) {
                        SwalCustomClass.fire({
                            text: usersMessages[body.message],
                            icon: 'error',
                        });
                    }
                });
            } else {
                const body: any = await asyncPostUser(formUser);
                if (body?.items !== undefined) {
                    navigate(`${USEREDIT}/${body.items.id}`);
                    SwalCustomClass.fire({
                        text: usersMessages.MESSAGE_NEW,
                        icon: 'success',
                    });
                } else if (body?.errors !== undefined) {
                    SwalCustomClass.fire({
                        text: usersMessages[body.message],
                        icon: 'error',
                    });
                }
            }
        },
    });

    const handleCheckboxChange = () => {
        const newValue = !formik.values.password_change;
        formik.setFieldValue('password_change', newValue);
        if (newValue) {
            setShowDialog(true);
        }
    };

    return (
        <>
            <FormView
                title={
                    idUser !== undefined
                        ? usersMessages.TITLE_EDIT
                        : usersMessages.TITLE_NEW
                }
                onClickSave={formik.handleSubmit}
                onClickBack={() => navigate(USERLIST)}
            />
            <Box mt={3} px={2}>
                <Grid
                    container
                    spacing={{ xs: 1.5, md: 1.5 }}
                    columnSpacing={{ xs: 1.5, sm: 1.5, md: 1.5 }}
                >
                    <Grid
                        item
                        xs={12}
                        md={idUserLog === idUser ? 1.9 : 2}
                        lg={idUserLog === idUser ? 1.9 : 2}
                    >
                        <FieldText
                            label="Código"
                            name="document"
                            size="small"
                            required={true}
                            touched={formik.touched.document}
                            errors={formik.errors.document}
                            value={formik.values.document}
                            onChange={formik.handleChange}
                        />
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        md={idUserLog === idUser ? 3.3 : 3.5}
                        lg={idUserLog === idUser ? 3.3 : 3.5}
                    >
                        <FieldText
                            label="Nombre"
                            name="name"
                            size="small"
                            required={true}
                            touched={formik.touched.name}
                            errors={formik.errors.name}
                            value={formik.values.name}
                            onChange={formik.handleChange}
                        />
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        md={idUserLog === idUser ? 3.3 : 3.5}
                        lg={idUserLog === idUser ? 3.3 : 3.5}
                    >
                        <FieldText
                            label="Apellido"
                            name="surname"
                            size="small"
                            required={true}
                            touched={formik.touched.surname}
                            errors={formik.errors.surname}
                            value={formik.values.surname}
                            onChange={formik.handleChange}
                        />
                    </Grid>
                    {idUserLog !== idUser ? (
                        <Grid item xs={12} md={3} lg={3}>
                            <FieldSelect
                                data={profiles}
                                label="Pérfil"
                                name="type"
                                size="small"
                                required={true}
                                defineValue="value"
                                touched={formik.touched.type}
                                errors={formik.errors.type}
                                value={formik.values.type}
                                onChange={formik.handleChange}
                            />
                        </Grid>
                    ) : null}
                    <Grid
                        item
                        xs={12}
                        md={idUserLog === idUser ? 3.5 : 4}
                        lg={idUserLog === idUser ? 3.5 : 4}
                    >
                        <FieldText
                            label="Correo Usuario"
                            name="username"
                            size="small"
                            required={true}
                            touched={formik.touched.username}
                            errors={formik.errors.username}
                            value={formik.values.username}
                            onChange={formik.handleChange}
                        />
                    </Grid>
                    <Grid item xs={12} md={4} lg={4}>
                        <FieldText
                            label="Contraseña"
                            name="password"
                            type="password"
                            size="small"
                            required={true}
                            touched={formik.touched.password}
                            errors={formik.errors.password}
                            value={formik.values.password}
                            onChange={formik.handleChange}
                        />
                    </Grid>
                    <Grid item xs={12} md={4} lg={4}>
                        <FieldText
                            label="Confirmar Contraseña"
                            name="password_retry"
                            type="password"
                            size="small"
                            required={true}
                            touched={formik.touched.password_retry}
                            errors={formik.errors.password_retry}
                            value={formik.values.password_retry}
                            onChange={formik.handleChange}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Checkbox
                            label="Actualización de contraseña"
                            checked={Boolean(formik.values.password_change)}
                            onChange={handleCheckboxChange}
                            isCheckMode={false}
                        />
                    </Grid>
                </Grid>
            </Box>
            <Dialog
                title="Se ha activado la actualización periódica de contraseña"
                open={showDialog}
                onClose={() => setShowDialog(false)}
            >
                <Grid
                    container
                    direction="column"
                    alignItems="center"
                    spacing={2}
                >
                    <Grid item>
                        <Box textAlign="center" px={3}>
                            <Typography>
                                <p>
                                    La contraseña ahora expirará cada{' '}
                                    <strong>30 días.</strong>
                                </p>
                                <p>
                                    El usuario deberá conocer el{' '}
                                    <strong>correo electrónico</strong> con el
                                    que accede al CRM ({formik.values.username}
                                    ), así como la{' '}
                                    <strong> contraseña actual</strong>, para
                                    poder realizar la actualización de su clave.
                                </p>
                            </Typography>
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                mt={3}
                            >
                                Puedes modificar esta configuración en cualquier
                                momento
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </Dialog>
        </>
    );
};

export default UserForm;
